import { ShieldCheck } from 'lucide-react'
import { useSearchParams } from 'react-router'
import { AdminBillingAuditTab } from './admin/AdminBillingAuditTab'
import { AdminBillingOverviewTab } from './admin/AdminBillingOverviewTab'
import { AdminBillingPhonesTab } from './admin/AdminBillingPhonesTab'
import { AdminBillingStripeTab } from './admin/AdminBillingStripeTab'
import { AdminBillingStorageTab } from './admin/AdminBillingStorageTab'

type TabKey = 'overview' | 'stripe' | 'storage' | 'phones' | 'audit'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'stripe', label: 'Stripe' },
  { key: 'storage', label: 'Storage' },
  { key: 'phones', label: 'Phones' },
  { key: 'audit', label: 'Audit log' },
]

/**
 * Tab shell for /admin. The original P&L body now lives in
 * AdminBillingOverviewTab; the Stripe + Storage tabs are new in this
 * branch. Audit log surfaces every billing/pricing mutation written to
 * ``billing_audit_log`` by the superadmin/tenant routes. The active
 * tab is read from ``?tab=overview|stripe|storage|audit``; a
 * missing/unknown value falls through to overview without redirecting,
 * so existing /admin bookmarks keep working.
 */
export function AdminBillingPage() {
  const [params, setParams] = useSearchParams()
  const raw = params.get('tab')
  const tab: TabKey =
    raw === 'stripe' ||
    raw === 'storage' ||
    raw === 'phones' ||
    raw === 'audit'
      ? raw
      : 'overview'

  function setTab(next: TabKey) {
    const p = new URLSearchParams(params)
    if (next === 'overview') p.delete('tab')
    else p.set('tab', next)
    setParams(p)
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <ShieldCheck className="h-6 w-6 text-primary" />
          Billing &amp; Pricing
        </h1>
        <p className="text-sm text-text-secondary">
          Superadmin view. Cost is what you pay VAPI + Twilio. Price is what
          customers are billed by Stripe at the configured per-minute and
          storage rates. Stripe tab manages products/prices/meters and
          per-org subscription items. Storage tab shows the daily byte
          snapshots that drive the storage meter.
        </p>
        <nav className="mt-4 inline-flex overflow-hidden rounded-lg border border-border bg-surface">
          {TABS.map((t) => {
            const active = t.key === tab
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-primary/15 text-primary'
                    : 'text-text-secondary hover:bg-surface-elevated'
                }`}
              >
                {t.label}
              </button>
            )
          })}
        </nav>
      </header>

      {tab === 'overview' && <AdminBillingOverviewTab />}
      {tab === 'stripe' && <AdminBillingStripeTab />}
      {tab === 'storage' && <AdminBillingStorageTab />}
      {tab === 'phones' && <AdminBillingPhonesTab />}
      {tab === 'audit' && <AdminBillingAuditTab />}
    </div>
  )
}
